import { XMLNS, XLINKS } from "./constants";
import {
  mapEl,
  loseLifeDialogEl,
  previousEpisodeEl,
  nextEpisodeEl,
  episodeTextEl,
  endEpisodesEl,
} from "./elements";
import { setLevelIndicators } from "./levels";
import { createCell, shakeCell, CELL_STATE } from "./cell";
import store from "./store";
import { addTouchEvent } from "./events";
import { newGame } from "./game";
import { setColors } from "./style";
import { closeMenu } from "./menu";
import { handleLivesModal, loseLife, updateLives } from "./lives";
import { logEvent } from "./analytics";
import { showMainMenu } from "./mainMenu";
import { POSITIONS } from "./info";
import { addClickToBtn } from "./sound";
import { getEpisode, getLevelIdx } from "./levels/episodes";
import { LEVELS } from "./levels/definitions";
import { stack } from "./nav";
import { GAME_MODE, gameState } from "./game/gameState";
import { updateStyles } from "./nav/styles";
import { setTimeoutAsync } from "./promise";
import { autoTranslate, resetLangAttributeOfLeafs } from "./translate";
import { showInfo } from "./info/modal";

const svgPath = (points, command) => {
  // build the d attributes by looping over the points
  return points.reduce(
    (acc, point, i, a) =>
      i === 0
        ? // if first point
          `M ${point[0]},${point[1]}`
        : // else
          `${acc} ${command(point, i, a)}`,
    ""
  );
};
const line = (pointA, pointB) => {
  const lengthX = pointB[0] - pointA[0];
  const lengthY = pointB[1] - pointA[1];
  return {
    length: Math.sqrt(Math.pow(lengthX, 2) + Math.pow(lengthY, 2)),
    angle: Math.atan2(lengthY, lengthX),
  };
};

const controlPoint = (current, previous, next, reverse) => {
  // When 'current' is the first or last point of the array
  // 'previous' or 'next' don't exist.
  // Replace with 'current'
  const p = previous || current;
  const n = next || current;
  // The smoothing ratio
  const smoothing = 0.2;
  // Properties of the opposed-line
  const o = line(p, n);
  // If is end-control-point, add PI to the angle to go backward
  const angle = o.angle + (reverse ? Math.PI : 0);
  const length = o.length * smoothing;
  // The control point position is relative to the current point
  const x = current[0] + Math.cos(angle) * length;
  const y = current[1] + Math.sin(angle) * length;
  return [x, y];
};

const bezierCommand = (point, i, a) => {
  // start control point
  const [cpsX, cpsY] = controlPoint(a[i - 1], a[i - 2], point);
  // end control point
  const [cpeX, cpeY] = controlPoint(point, a[i - 1], a[i + 1], true);
  return `C ${cpsX},${cpsY} ${cpeX},${cpeY} ${point[0]},${point[1]}`;
};

export function drawMap() {
  const episodes = getEpisode(store.episodeIdx);
  if (episodes === null) return drawEndEpisodes();
  else hideEndEpisodes();
  const d = svgPath(
    [[-6, 5], ...episodes, [30, 5]].map(([x, y]) => [
      x * 22,
      (y * 22 + 60) * 0.8 + 57,
    ]),
    bezierCommand
  );

  const SCALE = 1.2;
  const FONT_SCALE = 0.9 * SCALE;
  const CAT_SCALE = 1.1 * SCALE;
  const PAW_SCALE = 0.23 * SCALE;
  const LOCK_SCALE = 0.17 * SCALE;

  const pathEl = document.createElementNS(XMLNS, "path");
  pathEl.setAttributeNS(null, "fill", "none");
  pathEl.setAttributeNS(null, "stroke-width", `${3.3 * SCALE}`);
  pathEl.setAttributeNS(null, "stroke", "rgba(0,0,0,0.85)");
  pathEl.setAttributeNS(null, "d", d);
  pathEl.setAttributeNS(null, "stroke-linecap", "round");
  pathEl.setAttributeNS(null, "stroke-dasharray", "15 10 2 8");
  mapEl.appendChild(pathEl);

  let cat;
  const baseLevelIdx = getLevelIdx(store.episodeIdx);
  for (const [i, point] of getEpisode(store.episodeIdx).entries()) {
    const levelIdx = i + baseLevelIdx;
    const diskEl = document.createElementNS(XMLNS, "use");
    diskEl.setAttributeNS(XLINKS, "xlink:href", "#c0");
    // diskEl.setAttributeNS(null, "fill", store.themes[2]);
    diskEl.setAttributeNS(
      null,
      "transform",
      `translate(${point[0] * 22 - 6 + (-21 * SCALE) / 1.5},${
        (point[1] * 22 + 60) * 0.8 + 57 + 4 + (7 * SCALE) / 1.5
      }) scale(${FONT_SCALE * 0.9})`
    );
    mapEl.appendChild(diskEl);

    const textEl = document.createElementNS(XMLNS, "text");
    textEl.innerHTML = levelIdx + 1;
    textEl.setAttributeNS(null, "font-family", "Tangerine");
    textEl.setAttributeNS(null, "font-size", "11");
    textEl.setAttributeNS(null, "font-weight", "bold");
    textEl.setAttributeNS(null, "text-anchor", "middle");
    textEl.setAttributeNS(null, "dominant-baseline", "central");
    textEl.setAttributeNS(
      null,
      "transform",
      `translate(${point[0] * 22 - 6 + (9 * SCALE) / 1.5},${
        (point[1] * 22 + 60) * 0.8 + 57 + 4 + (24.5 * SCALE) / 1.5
      }) scale(${FONT_SCALE})`
    );
    textEl.classList.add("notranslate");
    mapEl.appendChild(textEl);

    const use = createCell(
      point[0] * 22 - 4 - 20.5 * SCALE,
      (point[1] * 22 + 60) * 0.8 + 57 + 4 - 20.5 * SCALE,
      store.maxLevelIdx == levelIdx
        ? CELL_STATE.NON_BLOCKABLE
        : store.maxLevelIdx > levelIdx
        ? CELL_STATE.FREE
        : CELL_STATE.BLOCKED,
      true
    );
    if (levelIdx <= store.maxLevelIdx)
      use.style.cursor = "url('img/paw.png'), pointer";
    use.setAttribute(
      "transform",
      `${use.getAttribute("transform")} scale(${SCALE})`
    );
    levelIdx > store.maxLevelIdx && use.setAttributeNS(null, "fill", "#7c8544");
    mapEl.appendChild(use);
    if (i == 0 && !store.episodesIntroShown) {
      showInfo({
        el: use,
        position: POSITIONS.RIGHT,
        text: "Embark on a multi-episode quest! Start by clicking the black cat for the first level.",
        persist: true,
        typeWriter: true,
      });
      store.set("episodesIntroShown", true);
    }

    let paw;
    if (levelIdx == store.levelIdx) {
      addClickToBtn(use);

      cat = document.createElementNS(XMLNS, "use");
      // cat.setAttributeNS(null, "class", "cat");
      cat.setAttributeNS(
        null,
        "transform",
        `translate(${point[0] * 22 - 6 - 43 * CAT_SCALE},${
          (point[1] * 22 + 60) * 0.8 + 57 + 4 - 66 * CAT_SCALE
        }) scale(${CAT_SCALE})`
      );
      cat.setAttributeNS(XLINKS, "xlink:href", "#cat-episodes");
    } else if (levelIdx <= store.maxLevelIdx) {
      addClickToBtn(use);

      paw = document.createElementNS(XMLNS, "use");
      paw.style.cursor = "url('img/paw.png'), pointer";
      paw.setAttributeNS(
        null,
        "transform",
        `translate(${point[0] * 22 - 4 - 32 * PAW_SCALE},${
          (point[1] * 22 + 60) * 0.8 + 57 + 4 - 60 * PAW_SCALE
        }) scale(${PAW_SCALE}) rotate(${Math.random() * 45 - 22.5}, 38, 48)`
      );
      paw.setAttributeNS(XLINKS, "xlink:href", "#paws");
      if (levelIdx == store.maxLevelIdx)
        paw.setAttributeNS(null, "opacity", 0.95);
      mapEl.appendChild(paw);
      addTouchEvent(paw, () => {
        shakeCell(use, true);
        shakeCell(textEl, true);
        shakeCell(diskEl, true);
        shakeCell(paw, true);
        play({ levelIdx });
      });
    } else {
      paw = document.createElementNS(XMLNS, "use");
      paw.setAttributeNS(
        null,
        "transform",
        `translate(${point[0] * 22 - 4 - 32 * LOCK_SCALE},${
          (point[1] * 22 + 60) * 0.8 + 54 + 4 - 60 * LOCK_SCALE
        }) scale(${LOCK_SCALE}) rotate(${Math.random() * 45 - 22.5}, 38, 48)`
      );
      paw.setAttributeNS(XLINKS, "xlink:href", "#lock");
      paw.setAttributeNS(null, "opacity", 0.6);
      mapEl.appendChild(paw);
    }
    levelIdx <= store.maxLevelIdx &&
      addTouchEvent(use, () => {
        shakeCell(use, true);
        shakeCell(textEl, true);
        shakeCell(diskEl, true);
        cat && shakeCell(cat, true);
        paw && shakeCell(paw, true);
        play({ levelIdx });
      });
  }
  cat && mapEl.appendChild(cat);
  // gameEl.style.background = `linear-gradient(217deg, rgba(255,0,0,.8), rgba(255,0,0,0) 70.71%), linear-gradient(127deg, rgba(0,255,0,.8), rgba(0,255,0,0) 70.71%), linear-gradient(336deg, rgba(0,0,255,.8), rgba(0,0,255,0) 70.71%)`;
  // gameEl.style.background = "#bbb2ac";
}

export function showMap(resizeOnly = false) {
  if (!resizeOnly) stack.push("map");
  checkEpisodesControls();
  mapEl.innerHTML = "";
  drawMap();
  setColors();
  updateStyles();
  setLevelIndicators();
  //   gameEl.setAttributeNS(null, "fill-opacity", 1);
  //   gameEl.setAttributeNS(null, "stroke-opacity", 1);
}

function _play({ levelIdx, level }) {
  if (levelIdx !== undefined) store.set("levelIdx", levelIdx);
  if (level === undefined) level = LEVELS[levelIdx];
  const levelCopy = JSON.parse(JSON.stringify(level));
  const screenState = { level: levelCopy };
  if (gameState.mode !== GAME_MODE.SKIN_DEMO)
    stack.push(
      gameState.mode == GAME_MODE.CLASSIC
        ? "game_classic"
        : gameState.mode == GAME_MODE.EDITOR
        ? "game_editor"
        : gameState.mode == GAME_MODE.COMMUNITY
        ? "game_community"
        : "game",
      screenState
    );
  newGame({ level });
}

export function play({ levelIdx, level, delay = 150 }) {
  if (!delay) _play({ levelIdx, level });
  else return setTimeoutAsync(_play, delay, { levelIdx, level });
}

export function showLoseLifeDialog(e) {
  e && e.stopImmediatePropagation();
  stack.push("lose_life_dialog");
  updateStyles();
}

export function closeLoseLifeDialog(e) {
  e && e.stopImmediatePropagation();
  stack.pop();
  updateStyles();
}

export function gotoMapOrMainMenu(e) {
  e && e.stopImmediatePropagation();
  if (loseLifeDialogEl.dataset.destination == "main_menu") {
    loseLife();
    closeLoseLifeDialog();
    showMainMenu(e, true);
    updateLives();
    return;
  }
  closeLoseLifeDialog();
  showMap();
  closeMenu();
  loseLife();
  handleLivesModal();
  updateLives();
  setLevelIndicators();
  logEvent("abort_game");
}

export function prevEpisode() {
  if (store.episodeIdx == 0) return;
  store.episodeIdx -= 1;
  showMap();
  nextEpisodeEl.style.visibility = "visible";
}
export function nextEpisode() {
  store.episodeIdx += 1;
  showMap();
  previousEpisodeEl.style.visibility = "visible";
}

function checkEpisodesControls() {
  episodeTextEl.innerHTML = `Episode ${store.episodeIdx + 1}`;
  if (store.episodeIdx > 0) previousEpisodeEl.style.visibility = "visible";
  if (store.episodeIdx == 0) previousEpisodeEl.style.visibility = "hidden";
  if (
    // store.maxLevelIdx !== getLevelIdx(NUM_EPISODES) &&
    getLevelIdx(store.episodeIdx) === null ||
    getLevelIdx(store.episodeIdx + 1) === null ||
    getLevelIdx(store.episodeIdx + 1) > store.maxLevelIdx
  )
    nextEpisodeEl.style.visibility = "hidden";
  resetLangAttributeOfLeafs(episodeTextEl);
  autoTranslate();
}

function drawEndEpisodes() {
  endEpisodesEl.style.display = "initial";
}
export function hideEndEpisodes() {
  endEpisodesEl.style.display = "none";
}
